import React, { useState } from 'react';
import ExportShareControls from './ExportShareControls';

const FamilyStats = ({ gedcomData, onViewTree }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!gedcomData) return null;

  const { individuals, families } = gedcomData;
  
  // Basic counts
  const totalPeople = Object.keys(individuals).length;
  const totalFamilies = Object.keys(families).length;
  
  // Gender analysis
  const genderStats = Object.values(individuals).reduce((acc, person) => {
    const gender = person.sex || 'Unknown';
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});
  
  // Date analysis
  const birthYears = Object.values(individuals)
    .map(person => person.birth)
    .filter(date => date && date.match(/\d{4}/))
    .map(date => parseInt(date.match(/\d{4}/)[0]))
    .sort((a, b) => a - b);
  
  const earliestYear = birthYears[0];
  const latestYear = birthYears[birthYears.length - 1];
  const yearSpan = latestYear - earliestYear;
  
  // Living vs deceased analysis
  const livingCount = Object.values(individuals).filter(person => !person.death).length;
  const deceasedCount = totalPeople - livingCount;
  
  // Generation analysis (by birth decades)
  const generationCounts = {};
  const centuryCounts = {};
  birthYears.forEach(year => {
    const decade = Math.floor(year / 10) * 10;
    const century = Math.floor(year / 100) * 100;
    generationCounts[decade] = (generationCounts[decade] || 0) + 1;
    centuryCounts[century] = (centuryCounts[century] || 0) + 1;
  });
  
  // Age analysis (for those with birth/death dates)
  const ages = Object.values(individuals)
    .filter(person => person.birth && person.death)
    .map(person => {
      const birthYear = parseInt(person.birth.match(/\d{4}/)?.[0]);
      const deathYear = parseInt(person.death.match(/\d{4}/)?.[0]);
      return deathYear - birthYear;
    })
    .filter(age => age > 0 && age < 120);
  
  const averageAge = ages.length > 0 ? Math.round(ages.reduce((sum, age) => sum + age, 0) / ages.length) : 0;
  
  // Location analysis
  const locations = Object.values(individuals)
    .flatMap(person => [person.birthPlace, person.deathPlace])
    .filter(place => place && place.trim())
    .reduce((acc, place) => {
      const cleanPlace = place.split(',').pop().trim(); // Get country/state
      acc[cleanPlace] = (acc[cleanPlace] || 0) + 1;
      return acc;
    }, {});
  
  const topLocations = Object.entries(locations)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);
  
  // Name analysis
  const surnames = Object.values(individuals)
    .map(person => person.name?.split(' ').pop())
    .filter(name => name)
    .reduce((acc, surname) => {
      acc[surname] = (acc[surname] || 0) + 1;
      return acc;
    }, {});
  
  const topSurnames = Object.entries(surnames)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);
  
  // Marriage analysis
  const marriageYears = Object.values(families)
    .map(family => family.marriage)
    .filter(date => date && date.match(/\d{4}/))
    .map(date => parseInt(date.match(/\d{4}/)[0]));
  
  const averageMarriageYear = marriageYears.length > 0 
    ? Math.round(marriageYears.reduce((sum, year) => sum + year, 0) / marriageYears.length)
    : 0;

  // Children analysis
  const childrenCounts = Object.values(families)
    .map(family => (family.children || []).length)
    .filter(count => count > 0);
  
  const averageChildren = childrenCounts.length > 0
    ? (childrenCounts.reduce((sum, count) => sum + count, 0) / childrenCounts.length).toFixed(1)
    : 0;
  
  const maxChildren = childrenCounts.length > 0 ? Math.max(...childrenCounts) : 0;

  const renderOverviewTab = () => (
    <div className="stats-content">
      <div className="stats-grid">
        <div className="stat-item highlight">
          <div className="stat-number">{totalPeople}</div>
          <div className="stat-label">Family Members</div>
          <div className="stat-detail">{livingCount} living, {deceasedCount} remembered</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-number">{totalFamilies}</div>
          <div className="stat-label">Family Units</div>
          <div className="stat-detail">{marriageYears.length} documented marriages</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-number">{yearSpan}+</div>
          <div className="stat-label">Years of History</div>
          <div className="stat-detail">{earliestYear} - {new Date().getFullYear()}</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-number">{Math.ceil(yearSpan / 25)}</div>
          <div className="stat-label">Generations</div>
          <div className="stat-detail">Spanning {Object.keys(centuryCounts).length} centuries</div>
        </div>
      </div>

      <div className="quick-insights">
        <div className="insight-card">
          <h4><i className="fas fa-users"></i> Demographics</h4>
          <div className="demographic-bars">
            {Object.entries(genderStats).map(([gender, count]) => (
              <div key={gender} className="demo-bar">
                <span className="demo-label">{gender}</span>
                <div className="demo-progress">
                  <div 
                    className="demo-fill" 
                    style={{width: `${(count / totalPeople) * 100}%`}}
                  ></div>
                </div>
                <span className="demo-count">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="insight-card">
          <h4><i className="fas fa-heart"></i> Family Life</h4>
          <ul className="insight-list">
            <li><strong>Average children per family:</strong> {averageChildren}</li>
            <li><strong>Largest family:</strong> {maxChildren} children</li>
            <li><strong>Average marriage year:</strong> {averageMarriageYear}</li>
            <li><strong>Average lifespan:</strong> {averageAge} years</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderLocationTab = () => (
    <div className="stats-content">
      <div className="location-analysis">
        <h4><i className="fas fa-globe"></i> Geographic Distribution</h4>
        <div className="location-grid">
          {topLocations.map(([location, count], index) => (
            <div key={location} className="location-item">
              <div className="location-rank">#{index + 1}</div>
              <div className="location-info">
                <div className="location-name">{location}</div>
                <div className="location-count">{count} family members</div>
                <div className="location-bar">
                  <div 
                    className="location-fill" 
                    style={{width: `${(count / topLocations[0][1]) * 100}%`}}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="migration-story">
          <h5>Migration Story</h5>
          <p>The Rossetti family journey began in <strong>Italy</strong> with Pasquale Rossetti in 1866, 
          spreading across continents to establish roots in <strong>Canada</strong> and the <strong>United States</strong>. 
          Today, family members can be found in {Object.keys(locations).length} different locations, 
          maintaining connections across {Math.max(...Object.values(locations))} generations.</p>
        </div>
      </div>
    </div>
  );

  const renderTimelineTab = () => (
    <div className="stats-content">
      <div className="timeline-analysis">
        <h4><i className="fas fa-clock"></i> Family Timeline</h4>
        
        <div className="century-breakdown">
          {Object.entries(centuryCounts)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([century, count]) => (
            <div key={century} className="century-card">
              <div className="century-header">
                <h5>{century}s - {parseInt(century) + 99}</h5>
                <span className="century-count">{count} births</span>
              </div>
              <div className="century-bar">
                <div 
                  className="century-fill" 
                  style={{width: `${(count / Math.max(...Object.values(centuryCounts))) * 100}%`}}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="milestone-events">
          <h5>Key Milestones</h5>
          <div className="milestone-grid">
            <div className="milestone">
              <div className="milestone-year">{earliestYear}</div>
              <div className="milestone-event">Pasquale Rossetti born in Italy</div>
            </div>
            <div className="milestone">
              <div className="milestone-year">{averageMarriageYear}</div>
              <div className="milestone-event">Peak marriage period</div>
            </div>
            <div className="milestone">
              <div className="milestone-year">{new Date().getFullYear()}</div>
              <div className="milestone-event">{livingCount} family members today</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNamesTab = () => (
    <div className="stats-content">
      <div className="names-analysis">
        <h4><i className="fas fa-signature"></i> Family Names</h4>
        
        <div className="surnames-grid">
          {topSurnames.map(([surname, count], index) => (
            <div key={surname} className="surname-card">
              <div className="surname-rank">#{index + 1}</div>
              <div className="surname-info">
                <div className="surname-name">{surname}</div>
                <div className="surname-count">{count} family members</div>
                <div className="surname-percentage">
                  {((count / totalPeople) * 100).toFixed(1)}% of family
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="name-insights">
          <h5>Name Heritage</h5>
          <div className="heritage-facts">
            <div className="heritage-fact">
              <strong>Most common surname:</strong> {topSurnames[0]?.[0]} ({topSurnames[0]?.[1]} members)
            </div>
            <div className="heritage-fact">
              <strong>Name diversity:</strong> {Object.keys(surnames).length} different surnames
            </div>
            <div className="heritage-fact">
              <strong>Rossetti legacy:</strong> {surnames['Rossetti'] || 0} direct Rossetti descendants
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="family-stats-card enhanced">
      <div className="stats-header">
        <h3>Rossetti Family Analytics</h3>
        <p>Comprehensive insights into our family heritage</p>
        
        {/* Export Controls */}
        <div className="stats-export-controls">
          <ExportShareControls 
            data={{
              totalPeople,
              totalFamilies,
              demographics: genderStats,
              generations: Object.keys(centuryCounts).length,
              yearSpan,
              livingCount,
              deceasedCount
            }}
            contentType="family_stats"
            title="Family Statistics Report"
            showBackupControls={false}
            className="family-stats-export"
          />
        </div>
      </div>
      
      <div className="stats-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-pie"></i> Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'locations' ? 'active' : ''}`}
          onClick={() => setActiveTab('locations')}
        >
          <i className="fas fa-map-marked-alt"></i> Locations
        </button>
        <button 
          className={`tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          <i className="fas fa-history"></i> Timeline
        </button>
        <button 
          className={`tab-btn ${activeTab === 'names' ? 'active' : ''}`}
          onClick={() => setActiveTab('names')}
        >
          <i className="fas fa-id-card"></i> Names
        </button>
      </div>

      <div className="stats-body">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'locations' && renderLocationTab()}
        {activeTab === 'timeline' && renderTimelineTab()}
        {activeTab === 'names' && renderNamesTab()}
      </div>

      <div className="stats-footer">
        <button className="explore-tree-btn primary" onClick={onViewTree}>
          <i className="fas fa-sitemap"></i>
          Explore Interactive Family Tree (beta)
        </button>
      </div>
    </div>
  );
};

export default FamilyStats;
